import React from 'react';
import { Check } from 'lucide-react';

import FieldError from '@/components/ui/field-error';
import { PhonePage, PrimaryButton, Spinner, TopBar } from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';

/**
 * Adding or correcting a customer, on a phone.
 *
 * The desktop asks in a side pane over the list; a handset has no side, so the
 * same eight questions take the whole screen, label over a 48px control, with
 * Save at the top and again at the bottom where the thumb ends up.
 */

const FIELDS = [
  ['firm_name', 'Firm', 'Company / business', 'customer-firm-input'],
  ['customer_name', 'Customer name *', 'Contact person', 'customer-name-input', 'words'],
  ['mobile_number', 'Mobile', '10-digit number', 'customer-mobile-input', 'tel'],
  ['email', 'Email', 'example@domain.com', 'customer-email-input', 'email'],
];

const ADDRESSES = [
  ['billing_address', 'Billing address', 'customer-billing-input'],
  ['delivery_address', 'Delivery address', 'customer-delivery-input'],
  ['notes', 'Notes', 'customer-notes-input'],
];

export default function PhoneCustomerForm({ mode, form, fieldErrors, onField, saving, onSave, onCancel }) {
  const adding = mode === 'add';
  return (
    <PhonePage testID="customer-form-page">
      <TopBar
        eyebrow="CUSTOMER"
        title={adding ? 'Add customer' : `Edit ${mode?.customer_name || 'customer'}`}
        onBack={onCancel}
        actions={(
          <button
            type="button"
            data-testid="save-customer-btn"
            onClick={onSave}
            disabled={saving}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
            style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
          >
            {saving ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : <><Check className="h-4 w-4" /> Save</>}
          </button>
        )}
      />

      <div className="flex-1 space-y-3 px-4 pb-10 pt-4">
        {FIELDS.map(([key, label, placeholder, testID, inputMode]) => (
          <Field key={key} label={label}>
            <input
              data-testid={testID}
              className={INPUT_CLASS}
              style={inputStyle}
              value={form[key]}
              placeholder={placeholder}
              inputMode={inputMode === 'words' ? undefined : inputMode}
              autoCapitalize={inputMode === 'words' ? 'words' : 'none'}
              onChange={(e) => onField(key, e.target.value)}
            />
            <FieldError error={fieldErrors[key]} />
          </Field>
        ))}

        <Field label="GSTIN">
          <input
            data-testid="customer-gstin-input"
            className={`${INPUT_CLASS} font-mono`}
            style={inputStyle}
            value={form.gstin}
            placeholder="15 characters — e.g. 27AABCG1234Q1Z5"
            autoCapitalize="characters"
            onChange={(e) => onField('gstin', e.target.value.toUpperCase())}
          />
          <FieldError error={fieldErrors.gstin} />
        </Field>

        {ADDRESSES.map(([key, label, testID]) => (
          <Field key={key} label={label}>
            <textarea
              data-testid={testID}
              className="min-h-[80px] w-full resize-y rounded-[10px] border px-3 py-2.5 text-[14px] outline-none"
              style={inputStyle}
              rows={2}
              value={form[key]}
              onChange={(e) => onField(key, e.target.value)}
            />
          </Field>
        ))}

        <PrimaryButton className="mt-5" onClick={onSave} busy={saving} testID="save-customer-bottom">
          <Check className="h-4 w-4" /> {adding ? 'Create customer' : 'Save changes'}
        </PrimaryButton>
      </div>
    </PhonePage>
  );
}
